import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

export const pageSx: SxProps<Theme> = {
    pb: 4,
};

/**
 * The three-section switch.
 *
 * Deliberately plain: the tab styling other screens use is the MUI default with a `neutral[500]`
 * label, and matching it matters more than any refinement of my own would.
 */
export const tabsSx: SxProps<Theme> = {
    mb: 2.5,
    '& .MuiTab-root': {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.9375rem',
        minHeight: 48,
        color: theme.palette.neutral[500],
    },
    '& .Mui-selected': {
        color: `${theme.palette.primary.main} !important`,
    },
    '& .MuiTabs-indicator': {
        height: 3,
        borderRadius: '3px 3px 0 0',
        backgroundColor: theme.palette.primary.main,
    },
};

/**
 * The bordered list card.
 *
 * `neutral[200]` rather than `off.main`: the former is the border every other list and control in the
 * application draws itself with, and matching it is most of what makes a new screen look like it
 * belongs.
 */
export const cardSx: SxProps<Theme> = {
    border: `1px solid ${theme.palette.neutral[200]}`,
    borderRadius: '12px',
    backgroundColor: theme.palette.background.default,
    overflow: 'hidden',
};

/**
 * The scrolling part of a list card.
 *
 * A directory can hold thousands of people, and an unbounded list pushed its own footer past the fold —
 * so the pagination that would have made it navigable was the one thing you could not reach. Bounded
 * here rather than on the page so the card keeps its own scrollbar and the footer stays put.
 */
export const listScrollSx: SxProps<Theme> = {
    // One height, full or empty. A range let the card shrink to its contents, so filtering down to a
    // few people - or to none - collapsed the table and moved everything below it, which reads as the
    // page rearranging itself rather than as a shorter answer to the same question.
    // One height, full or empty, from a desktop up. Below it the card gives up its own scroller and
    // the page scrolls: 320px of bounded list on a phone shows three rows inside a box inside a page,
    // and `vh` counted the address bar on top of that.
    height: {xs: 'auto', md: 'calc(100dvh - 400px)'},
    minHeight: {xs: 'unset', md: '320px'},
    overflowY: {xs: 'visible', md: 'auto'},
};

/** Matches the footer of every other table in the app, so this reads as the same control. */
export const listFooterSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    px: 2,
    py: 1.25,
    borderTop: `1px solid ${theme.palette.neutral[200]}`,
    backgroundColor: '#FAFBFD',
    flexWrap: 'wrap',
};

export const emptySx: SxProps<Theme> = {
    px: 2.5,
    py: 5,
    textAlign: 'center',
    color: theme.palette.grey[500],
};

/**
 * The paragraph under a heading that says what the section is for.
 *
 * `grey[500]`, never `text.secondary` — the latter is `#fff` in this theme, which is the colour for
 * text on a dark surface and renders invisible here.
 */
export const sectionHintSx: SxProps<Theme> = {
    mb: 2,
    fontSize: '0.875rem',
    color: theme.palette.grey[500],
    maxWidth: 760,
    lineHeight: 1.5,
};
